import { useCallback, useEffect, useState } from 'react';
import { MakeToast } from '../../../../components';
import { getAllModels } from '../../llm/services/llm';
import {
  ROUTING_BAND_META,
  RouterBandAssignment,
  getRouterBands,
  saveRouterBands,
  syncRouterBandsFromModels,
} from '../../chat/services/routing';
import { isAutoRoutingFeatureEnabled } from '../../chat/lib/routing-mode';

type ModelOption = { id: number; name: string; routing_band?: number | null };

function RoutingBandsSettings() {
  const enabled = isAutoRoutingFeatureEnabled();
  const [models, setModels] = useState<ModelOption[]>([]);
  const [bands, setBands] = useState<Record<number, number | ''>>({
    1: '',
    2: '',
    3: '',
    4: '',
    5: '',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const [modelsRes, bandsRes] = await Promise.all([getAllModels(), getRouterBands()]);
      const modelList = modelsRes?.data?.data?.models ?? [];
      setModels(
        modelList.map((m: { id: number; name: string; routing_band?: number | null }) => ({
          id: m.id,
          name: m.name,
          routing_band: m.routing_band ?? null,
        })),
      );
      const assignments: RouterBandAssignment[] = bandsRes?.data?.bands ?? [];
      const next: Record<number, number | ''> = { 1: '', 2: '', 3: '', 4: '', 5: '' };
      assignments.forEach((row) => {
        if (row.model_id) {
          next[row.band] = row.model_id;
        }
      });
      setBands(next);
    } catch {
      MakeToast('error', 'Failed to load routing band configuration');
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    load();
  }, [load]);

  if (!enabled) {
    return null;
  }

  const handleSave = async () => {
    const payload = ROUTING_BAND_META.map(({ band }) => ({
      band,
      model_id: bands[band],
    })).filter((row) => typeof row.model_id === 'number' && row.model_id > 0) as {
      band: number;
      model_id: number;
    }[];

    if (payload.length < 5) {
      MakeToast('error', 'Assign a model to each band (1–5) before saving');
      return;
    }

    setSaving(true);
    try {
      await saveRouterBands(payload);
      MakeToast('success', 'Auto-routing band map saved');
      await load();
    } catch {
      MakeToast('error', 'Could not save band configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async () => {
    setSaving(true);
    try {
      await syncRouterBandsFromModels();
      MakeToast('success', 'Synced bands from models.routing_band + defaults');
      await load();
    } catch {
      MakeToast('error', 'Sync failed — run add_routing_band_tables.sql first');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-xl border border-[#E4E4E7] bg-white p-6 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-[#101828]">Auto model routing (5 bands)</h2>
          <p className="text-xs text-[#6A6A6A] mt-1 max-w-2xl">
            SCX scores each message (0–1). Each model&apos;s band comes from its{' '}
            <strong>model score</strong> on the LLM admin page. Assign one model per band for
            defaults; chat Auto routing picks among the user&apos;s allowed models in that band.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-[#E4E4E7] text-[#132961] hover:bg-[#F4F4F5]"
            onClick={handleSync}
            disabled={saving || loading}
          >
            Sync from DB
          </button>
          <button
            type="button"
            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-[#132961] text-white hover:opacity-90 disabled:opacity-50"
            onClick={handleSave}
            disabled={saving || loading}
          >
            Save bands
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-[#71717A]">Loading…</p>
      ) : (
        <div className="space-y-3">
          {ROUTING_BAND_META.map(({ band, range, hint }) => (
            <div
              key={band}
              className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-2 items-center border-b border-[#F4F4F5] pb-3 last:border-0"
            >
              <div>
                <p className="text-sm font-semibold text-[#132961]">Band {band}</p>
                <p className="text-[10px] text-[#71717A]">{range}</p>
                <p className="text-[10px] text-[#A1A1AA]">{hint}</p>
              </div>
              <select
                className="w-full border border-[#E4E4E7] rounded-lg px-3 py-2 text-sm bg-white"
                value={bands[band]}
                onChange={(e) => {
                  const val = e.target.value ? Number(e.target.value) : '';
                  setBands((prev) => ({ ...prev, [band]: val }));
                }}
              >
                <option value="">Select model…</option>
                {[...models]
                  .sort((a, b) => {
                    const aMatch = a.routing_band === band ? 0 : 1;
                    const bMatch = b.routing_band === band ? 0 : 1;
                    if (aMatch !== bMatch) {
                      return aMatch - bMatch;
                    }
                    return a.name.localeCompare(b.name);
                  })
                  .map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                      {m.routing_band != null ? ` · DB band ${m.routing_band}` : ''}
                      {m.routing_band === band ? ' ✓' : ''}
                    </option>
                  ))}
              </select>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default RoutingBandsSettings;
